import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GripVertical, AlertCircle, MessageSquare, Lightbulb, Settings, Rocket } from "lucide-react";

// Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_team-automation-hub/artifacts/43177upw_agents2go_white.svg";

// Employee Images
const MIA_IMAGE = "https://customer-assets.emergentagent.com/job_team-automation-hub/artifacts/2qv2td0w_MIA_Senior%20Marketing%20Managerin.png";
const ANNA_IMAGE = "https://customer-assets.emergentagent.com/job_team-automation-hub/artifacts/pd7wnpaz_ANNA_Junior%20Marketing%20Managerin.png";

// Feature Libraries by Department
const FEATURE_LIBRARIES = {
  marketing: {
    title: "Marketing Features",
    basic: [
      "Marktanalyse & Konkurrenzbeobachtung",
      "KPI Monitoring",
      "Performance Dashboard (automatisiert)",
      "PR Monitoring",
      "Community Management",
      "Kampagnenplanung & Content-Strategie",
      "Wettbewerbs Alert System",
      "Lead Intelligence (Light)"
    ],
    pro: [
      "Social Organic Management",
      "Social Ads Management",
      "Google Ads Management",
      "E-Mail Marketing & Automation",
      "CRO (Conversion Rate Optimierung)",
      "SEO / GEO Optimierung",
      "Website Optimierung",
      "Marketing Budget Management"
    ]
  },
  vertrieb: {
    title: "Vertrieb Features",
    basic: [
      "Lead Qualifizierung",
      "CRM Datenpflege",
      "Pipeline Monitoring",
      "Angebotsvorbereitung",
      "Follow-up Management",
      "Vertriebs KPI Monitoring",
      "Wettbewerbsanalyse Vertrieb",
      "Lead Segmentierung"
    ],
    pro: [
      "Sales Forecasting",
      "Deal Analyse",
      "Angebotsstrategie",
      "Cross-Selling Analyse",
      "Vertriebsperformance Dashboard",
      "Preisstrategie Analyse",
      "Verkaufsprozess Optimierung",
      "Revenue Forecast Modelle"
    ]
  },
  controlling: {
    title: "Controlling Features",
    basic: [
      "KPI Monitoring",
      "Finanzreport Erstellung",
      "Budgetübersicht",
      "Kostenanalyse",
      "Forecast Vorbereitung",
      "Datenaufbereitung",
      "Abweichungsanalysen",
      "Management Reports"
    ],
    pro: [
      "Szenarioanalysen",
      "Unternehmensperformance Analyse",
      "Cashflow Prognosen",
      "Strategische Finanzanalyse",
      "Profitabilitätsmodelle",
      "Investitionsanalysen",
      "Risikoanalysen",
      "Business Forecast Modelle"
    ]
  },
  legal: {
    title: "Legal Features",
    basic: [
      "Vertragsprüfung",
      "Dokumentenmanagement",
      "Fristenüberwachung",
      "Standard Vertragsanalyse",
      "Compliance Monitoring",
      "Vertragsarchiv",
      "Risikohinweise",
      "Dokumentenkategorisierung"
    ],
    pro: [
      "Rechtliche Risikoanalyse",
      "Compliance Audits",
      "DSGVO Monitoring",
      "Vertragsstrategie Analyse",
      "Haftungsprüfung",
      "Regulatorische Analyse",
      "Vertragsoptimierung",
      "Entscheidungsunterstützung"
    ]
  },
  office: {
    title: "Office / Bürokauffrau Features",
    basic: [
      "Dokumentenerstellung",
      "Datenpflege",
      "Terminorganisation",
      "Meetingprotokolle",
      "Interne Kommunikation",
      "Dokumentenablage",
      "Workflow Organisation",
      "Task Koordination"
    ],
    pro: [
      "Prozessoptimierung",
      "Workflow Automatisierung",
      "Reporting Unterstützung",
      "Management Reports",
      "Projektkoordination",
      "Informationsmanagement",
      "Strukturierung interner Abläufe",
      "Effizienzanalysen"
    ]
  },
  assistenz: {
    title: "Assistenz der Geschäftsführung Features",
    basic: [
      "Terminmanagement",
      "Meetingvorbereitung",
      "Recherche Aufgaben",
      "Präsentationsvorbereitung",
      "Interne Abstimmung",
      "Informationsaufbereitung",
      "Task Priorisierung",
      "Management Unterstützung"
    ],
    pro: [
      "Wettbewerbsanalysen",
      "Marktrecherche",
      "Strategische Entscheidungsunterlagen",
      "Projektkoordination",
      "Management Reporting",
      "Unternehmensanalysen",
      "Strategie Monitoring",
      "Business Insights"
    ]
  }
};

// Marketing Features for Configurator
const BASIC_FEATURES = FEATURE_LIBRARIES.marketing.basic.map((name, i) => ({ id: `b${i+1}`, name }));
const PRO_FEATURES = FEATURE_LIBRARIES.marketing.pro.map((name, i) => ({ id: `p${i+1}`, name }));

// Initial feature sets
const MIA_INITIAL_BASIC = ["b1", "b2", "b3", "b4", "b5", "b6", "b7"];
const MIA_INITIAL_PRO = ["p1", "p2", "p3", "p4", "p5"];
const ANNA_INITIAL_BASIC = ["b1", "b2", "b3", "b4", "b5"];
const ANNA_INITIAL_PRO = ["p1"];

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`} data-testid="navbar">
      <div className="container-custom flex items-center justify-between">
        <a href="/" data-testid="nav-logo">
          <img src={LOGO_URL} alt="agents2go" className="logo-img" />
        </a>
        <button 
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Zurück zur Startseite</span>
        </button>
      </div>
    </nav>
  );
};

// Feature Library Section Component
const FeatureLibrarySection = ({ library }) => {
  return (
    <div className="feature-library-section">
      <h3 className="fls-title">{library.title}</h3>
      <div className="fls-grid">
        <div className="fls-column">
          <h4 className="fls-column-title basic">Basic Features</h4>
          <ul className="fls-list">
            {library.basic.map((feature, index) => (
              <li key={index} className="fls-item basic">{feature}</li>
            ))}
          </ul>
        </div>
        <div className="fls-column">
          <h4 className="fls-column-title pro">Pro Features</h4>
          <ul className="fls-list">
            {library.pro.map((feature, index) => (
              <li key={index} className="fls-item pro">{feature}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Process Step Component
const ProcessStep = ({ number, icon, title, description, link }) => {
  const content = (
    <>
      <div className="process-step-number">{number}</div>
      <div className="process-step-icon">{icon}</div>
      <h4 className="process-step-title">{title}</h4>
      <p className="process-step-desc">{description}</p>
    </>
  );

  if (link) {
    return (
      <a href={link} className="process-step process-step-link">
        {content}
      </a>
    );
  }

  return (
    <div className="process-step">
      {content}
    </div>
  );
};

// Feature Card Component for Configurator
const FeatureCard = ({ feature, type, isDragging, onDragStart, onDragEnd }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, feature, type)}
      onDragEnd={onDragEnd}
      className={`feature-card ${type} ${isDragging ? "dragging" : ""}`}
    >
      <GripVertical size={16} className="grip-icon" />
      <span>{feature.name}</span>
    </div>
  );
};

// Drop Zone Component
const DropZone = ({ title, features, type, maxItems, currentCount, onDrop, onRemove, showWarning }) => {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = () => setIsOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    onDrop(e);
  };

  return (
    <div className="drop-zone-container">
      <div className="drop-zone-header">
        <span className={`drop-zone-title ${type}`}>{title}</span>
        <span className="drop-zone-count">{currentCount}/{maxItems}</span>
      </div>
      <div
        className={`drop-zone ${type} ${isOver ? "drag-over" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {features.map((feature, index) => (
          <div key={feature.id} className={`assigned-feature ${type}`} onClick={() => onRemove(feature.id, type)}>
            <span className="feature-number">{index + 1}</span>
            <span className="feature-name">{feature.name}</span>
            <span className="remove-btn">×</span>
          </div>
        ))}
        {features.length === 0 && <div className="drop-placeholder">Features hierher ziehen</div>}
      </div>
      {showWarning && (
        <div className="max-warning">
          <AlertCircle size={16} />
          <span>Maximale Anzahl an Features erreicht.</span>
        </div>
      )}
    </div>
  );
};

// Employee Configurator Card
const EmployeeConfigurator = ({ name, role, image, description, basicFeatures, proFeatures, maxBasic, maxPro, onDropBasic, onDropPro, onRemoveBasic, onRemovePro, basicWarning, proWarning }) => {
  return (
    <div className="employee-configurator">
      <div className="employee-header">
        <img src={image} alt={name} className="employee-avatar" />
        <div>
          <h3 className="employee-name">{name}</h3>
          <p className="employee-role">{role}</p>
        </div>
      </div>
      <p className="employee-description">{description}</p>
      
      <DropZone
        title="Basic Features"
        features={basicFeatures}
        type="basic"
        maxItems={maxBasic}
        currentCount={basicFeatures.length}
        onDrop={onDropBasic}
        onRemove={onRemoveBasic}
        showWarning={basicWarning}
      />
      
      <DropZone
        title="Pro Features"
        features={proFeatures}
        type="pro"
        maxItems={maxPro}
        currentCount={proFeatures.length}
        onDrop={onDropPro}
        onRemove={onRemovePro}
        showWarning={proWarning}
      />
    </div>
  );
};

// Main Digital Team Page Component
function DigitalTeamPage() {
  const [draggedFeature, setDraggedFeature] = useState(null);
  const [draggedType, setDraggedType] = useState(null);
  
  // MIA state
  const [miaBasic, setMiaBasic] = useState(BASIC_FEATURES.filter(f => MIA_INITIAL_BASIC.includes(f.id)));
  const [miaPro, setMiaPro] = useState(PRO_FEATURES.filter(f => MIA_INITIAL_PRO.includes(f.id)));
  const [miaBasicWarning, setMiaBasicWarning] = useState(false);
  const [miaProWarning, setMiaProWarning] = useState(false);
  
  // ANNA state
  const [annaBasic, setAnnaBasic] = useState(BASIC_FEATURES.filter(f => ANNA_INITIAL_BASIC.includes(f.id)));
  const [annaPro, setAnnaPro] = useState(PRO_FEATURES.filter(f => ANNA_INITIAL_PRO.includes(f.id)));
  const [annaBasicWarning, setAnnaBasicWarning] = useState(false);
  const [annaProWarning, setAnnaProWarning] = useState(false);

  const handleDragStart = (e, feature, type) => {
    setDraggedFeature(feature);
    setDraggedType(type);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedFeature(null);
    setDraggedType(null);
  };

  // MIA handlers
  const handleMiaDropBasic = (e) => {
    e.preventDefault();
    if (!draggedFeature || draggedType !== "basic") return;
    if (miaBasic.length >= 7) {
      setMiaBasicWarning(true);
      setTimeout(() => setMiaBasicWarning(false), 3000);
      return;
    }
    if (!miaBasic.find(f => f.id === draggedFeature.id)) {
      setMiaBasic([...miaBasic, draggedFeature]);
    }
  };

  const handleMiaDropPro = (e) => {
    e.preventDefault();
    if (!draggedFeature || draggedType !== "pro") return;
    if (miaPro.length >= 5) {
      setMiaProWarning(true);
      setTimeout(() => setMiaProWarning(false), 3000);
      return;
    }
    if (!miaPro.find(f => f.id === draggedFeature.id)) {
      setMiaPro([...miaPro, draggedFeature]);
    }
  };

  const handleMiaRemoveBasic = (id) => setMiaBasic(miaBasic.filter(f => f.id !== id));
  const handleMiaRemovePro = (id) => setMiaPro(miaPro.filter(f => f.id !== id));

  // ANNA handlers
  const handleAnnaDropBasic = (e) => {
    e.preventDefault();
    if (!draggedFeature || draggedType !== "basic") return;
    if (annaBasic.length >= 5) {
      setAnnaBasicWarning(true);
      setTimeout(() => setAnnaBasicWarning(false), 3000);
      return;
    }
    if (!annaBasic.find(f => f.id === draggedFeature.id)) {
      setAnnaBasic([...annaBasic, draggedFeature]);
    }
  };

  const handleAnnaDropPro = (e) => {
    e.preventDefault();
    if (!draggedFeature || draggedType !== "pro") return;
    if (annaPro.length >= 1) {
      setAnnaProWarning(true);
      setTimeout(() => setAnnaProWarning(false), 3000);
      return;
    }
    if (!annaPro.find(f => f.id === draggedFeature.id)) {
      setAnnaPro([...annaPro, draggedFeature]);
    }
  };

  const handleAnnaRemoveBasic = (id) => setAnnaBasic(annaBasic.filter(f => f.id !== id));
  const handleAnnaRemovePro = (id) => setAnnaPro(annaPro.filter(f => f.id !== id));

  return (
    <div className="digital-team-page">
      <Navbar />
      
      {/* SECTION 1 – Einführung */}
      <section className="dt-section dt-intro">
        <div className="container-custom text-center">
          <h1 className="dt-headline">
            <span className="gradient-text">Digitale Mitarbeiter</span> für zentrale Unternehmensbereiche
          </h1>
          <p className="dt-subheadline">
            Jeder digitale Mitarbeiter übernimmt klar definierte Aufgaben innerhalb einer Rolle im Unternehmen. Es gibt zwei Ausprägungen.
          </p>
          
          <div className="dt-levels">
            <div className="dt-level">
              <span className="dt-level-name">Assistenz-Level</span>
              <span className="dt-level-tag">(Basic)</span>
            </div>
            <div className="dt-level">
              <span className="dt-level-name">Senior-Level</span>
              <span className="dt-level-tag">(Pro)</span>
            </div>
          </div>

          <div className="dt-cards-grid">
            <div className="dt-card basic">
              <h3 className="dt-card-title">Digital Employee Basic</h3>
              <p className="dt-card-desc">Digitale Assistenz für operative Aufgaben.</p>
              <div className="dt-card-features">
                <span className="dt-card-feature-label">Funktionsumfang:</span>
                <span className="dt-card-feature-value">5 Standard Features + 1 individuelles Modul</span>
              </div>
              <div className="dt-card-goal">
                <span className="dt-card-goal-label">Ziel:</span>
                <span>klare Entlastung im Tagesgeschäft.</span>
              </div>
            </div>
            
            <div className="dt-card pro">
              <h3 className="dt-card-title gradient-text">Digital Employee Pro</h3>
              <p className="dt-card-desc">Digitale Fachkraft mit strategischer Tiefe.</p>
              <div className="dt-card-features">
                <span className="dt-card-feature-label">Funktionsumfang:</span>
                <span className="dt-card-feature-value">7 Standard Features + 5 individuelle Module</span>
              </div>
              <div className="dt-card-goal">
                <span className="dt-card-goal-label">Ziel:</span>
                <span>Übernahme ganzer Prozesse und strategischer Aufgaben.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 – Feature Konzept */}
      <section className="dt-section dt-concept">
        <div className="container-custom text-center">
          <h2 className="dt-section-headline">Standardisierte <span className="gradient-text">Feature-Bibliothek</span></h2>
          <p className="dt-section-text">
            Digitale Mitarbeiter basieren auf einer strukturierten Feature-Bibliothek.<br />
            Jeder Unternehmensbereich verfügt über definierte Basic- und Pro-Features, die typische Aufgaben im Unternehmen abbilden.
          </p>
          
          <div className="dt-concept-levels">
            <div className="dt-concept-level">
              <span className="dt-concept-level-name">Assistenz-Level nutzt:</span>
              <span className="dt-concept-level-value">5 Basic Features + 1 Pro Feature</span>
            </div>
            <div className="dt-concept-level">
              <span className="dt-concept-level-name">Senior-Level nutzt:</span>
              <span className="dt-concept-level-value">7 Basic Features + 5 Pro Features</span>
            </div>
          </div>
          
          <p className="dt-concept-note">
            Die konkreten Features werden individuell für jedes Unternehmen zusammengestellt.
          </p>
        </div>
      </section>

      {/* SECTION 3 – Feature Bibliothek */}
      <section className="dt-section dt-library">
        <div className="container-custom">
          <h2 className="dt-section-headline text-center">
            Feature Bibliothek nach <span className="gradient-text">Unternehmensbereichen</span>
          </h2>
          <p className="dt-section-subtext text-center">
            Jeder Unternehmensbereich verfügt über eine eigene Bibliothek aus Basic- und Pro-Features.
          </p>
          
          <div className="dt-libraries">
            {Object.values(FEATURE_LIBRARIES).map((library, index) => (
              <FeatureLibrarySection key={index} library={library} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 – Hinweis */}
      <section className="dt-section dt-hint">
        <div className="container-custom text-center">
          <p className="dt-hint-text">
            Welche Features in Ihrem Unternehmen den größten Mehrwert schaffen, finden wir gemeinsam in einem strukturierten Workshop heraus.
          </p>
          <a
            href="mailto:kontakt@agents2go.com?subject=Kostenloses%20Erstgespräch%20vereinbaren"
            className="gradient-btn"
          >
            <span className="cta-text-desktop">Kostenloses Erstgespräch vereinbaren</span>
            <span className="cta-text-mobile">Gratis Termin buchen</span>
          </a>
        </div>
      </section>

      {/* SECTION 5 – So funktioniert die Zusammenarbeit */}
      <section className="dt-section dt-process">
        <div className="container-custom">
          <h2 className="dt-section-headline text-center">
            So funktioniert die <span className="gradient-text">Zusammenarbeit</span>
          </h2>
          
          <div className="process-grid">
            <ProcessStep
              number="1"
              icon={<MessageSquare size={28} />}
              title="Kostenloses Erstgespräch (30–45 Minuten)"
              description="Wir lernen Ihr Unternehmen kennen und identifizieren erste Potenziale für digitale Mitarbeiter."
              link="/erstgespraech"
            />
            <ProcessStep
              number="2"
              icon={<Lightbulb size={28} />}
              title="AI-Potential Workshop"
              description="Im Workshop analysieren wir Prozesse und definieren, welcher digitale Mitarbeiter den größten Nutzen bringt."
            />
            <ProcessStep
              number="3"
              icon={<Settings size={28} />}
              title="Digital Employee Setup"
              description="Der digitale Mitarbeiter wird individuell für Ihr Unternehmen konfiguriert und in Ihre Systeme integriert."
            />
            <ProcessStep
              number="4"
              icon={<Rocket size={28} />}
              title="Produktiver Einsatz"
              description="Der digitale Mitarbeiter übernimmt Aufgaben im Tagesgeschäft und entlastet Ihr Team."
            />
          </div>
          
          <div className="cta-box">
            <a
              href="mailto:kontakt@agents2go.com?subject=Kostenloses%20Erstgespräch%20vereinbaren"
              className="gradient-btn"
            >
              <span className="cta-text-desktop">Kostenloses Erstgespräch vereinbaren</span>
              <span className="cta-text-mobile">Gratis Termin buchen</span>
            </a>
            <p className="cta-subtext">Dauer ca. 30–45 Minuten.</p>
          </div>
        </div>
      </section>

      {/* SECTION 6 – Beispiel Konfigurator */}
      <section className="dt-section dt-configurator">
        <div className="container-custom">
          <h2 className="dt-section-headline text-center">
            So könnte Ihr <span className="gradient-text">digitaler Mitarbeiter</span> aussehen
          </h2>
          <p className="dt-section-text text-center">
            Digitale Mitarbeiter bestehen aus individuell zusammengestellten Features.<br />
            Hier sehen Sie ein Beispiel für einen Marketing-Mitarbeiter.
          </p>
          
          <p className="dt-config-hint text-center">
            Vorkonfigurierte Beispiele – Features können individuell angepasst werden.
          </p>

          <div className="dt-config-grid">
            {/* Feature Library */}
            <div className="feature-library">
              <h3 className="library-title">Alle Marketing Features</h3>
              
              <div className="library-section">
                <h4 className="library-section-title basic">Basic Features ({BASIC_FEATURES.length})</h4>
                <div className="library-features">
                  {BASIC_FEATURES.map((feature, index) => (
                    <FeatureCard
                      key={feature.id}
                      feature={{ ...feature, name: `${index + 1} ${feature.name}` }}
                      type="basic"
                      isDragging={draggedFeature?.id === feature.id}
                      onDragStart={(e) => handleDragStart(e, feature, "basic")}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </div>
              </div>
              
              <div className="library-section">
                <h4 className="library-section-title pro">Pro Features ({PRO_FEATURES.length})</h4>
                <div className="library-features">
                  {PRO_FEATURES.map((feature, index) => (
                    <FeatureCard
                      key={feature.id}
                      feature={{ ...feature, name: `${index + 1} ${feature.name}` }}
                      type="pro"
                      isDragging={draggedFeature?.id === feature.id}
                      onDragStart={(e) => handleDragStart(e, feature, "pro")}
                      onDragEnd={handleDragEnd}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* MIA Configurator */}
            <EmployeeConfigurator
              name="MIA"
              role="Digital Senior Marketing Manager"
              image={MIA_IMAGE}
              description="Skaliert Marketing und schafft Wachstum."
              basicFeatures={miaBasic}
              proFeatures={miaPro}
              maxBasic={7}
              maxPro={5}
              onDropBasic={handleMiaDropBasic}
              onDropPro={handleMiaDropPro}
              onRemoveBasic={handleMiaRemoveBasic}
              onRemovePro={handleMiaRemovePro}
              basicWarning={miaBasicWarning}
              proWarning={miaProWarning}
            />

            {/* ANNA Configurator */}
            <EmployeeConfigurator
              name="ANNA"
              role="Digital Marketing Assistenz"
              image={ANNA_IMAGE}
              description="Strukturiert und organisiert Marketing im Tagesgeschäft."
              basicFeatures={annaBasic}
              proFeatures={annaPro}
              maxBasic={5}
              maxPro={1}
              onDropBasic={handleAnnaDropBasic}
              onDropPro={handleAnnaDropPro}
              onRemoveBasic={handleAnnaRemoveBasic}
              onRemovePro={handleAnnaRemovePro}
              basicWarning={annaBasicWarning}
              proWarning={annaProWarning}
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="dt-section dt-final-cta">
        <div className="container-custom text-center">
          <h2>Bereit für Ihren digitalen Mitarbeiter?</h2>
          <p className="dt-cta-text">Vereinbaren Sie ein kostenloses Erstgespräch und entdecken Sie die Möglichkeiten.</p>
          <a
            href="mailto:kontakt@agents2go.com?subject=Kostenloses%20Erstgespräch%20vereinbaren"
            className="gradient-btn"
          >
            <span className="cta-text-desktop">Kostenloses Erstgespräch vereinbaren</span>
            <span className="cta-text-mobile">Gratis Termin buchen</span>
          </a>
          <p className="cta-subtext">Dauer ca. 30–45 Minuten.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer section-dark">
        <div className="container-custom text-center">
          <img src={LOGO_URL} alt="agents2go" className="logo-img mx-auto mb-6" />
          <p className="text-white/60">
            © 2026 agents2go · Ein Produkt von{" "}
            <a href="https://tk100x.com" target="_blank" rel="noopener noreferrer" className="footer-link">TK100X</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default DigitalTeamPage;
